import { captureMessage, withScope } from "@sentry/vue";

export type SharedSpaceUrlConfigKey =
  | "shared_space_url_prefix"
  | "default_space_prefix";

export type SharedSpaceUrlPrefixInvalidReason =
  | "not-string"
  | "empty"
  | "non-https"
  | "invalid-url"
  | "missing-hostname";

export type SharedSpaceUrlPrefixNormalization = {
  value: string | null;
  reason: SharedSpaceUrlPrefixInvalidReason | null;
  valueType: string;
};

type SharedSpaceUrlPrefixes = {
  sharedSpaceUrlPrefix: string;
  defaultSpacePrefix: string | null;
  hasBusinessCardVariants: boolean;
};

const reportedInvalidPrefixes = new Set<string>();

const getValueType = (value: unknown): string => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

export const normalizeSharedSpaceUrlPrefix = (
  value: unknown,
): SharedSpaceUrlPrefixNormalization => {
  const valueType = getValueType(value);
  if (typeof value !== "string") {
    return { value: null, reason: "not-string", valueType };
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { value: null, reason: "empty", valueType };
  }
  if (!/^https:\/\//i.test(trimmed)) {
    return { value: null, reason: "non-https", valueType };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch (_error) {
    return { value: null, reason: "invalid-url", valueType };
  }

  if (parsed.hostname.length === 0) {
    return { value: null, reason: "missing-hostname", valueType };
  }

  return { value: trimmed, reason: null, valueType };
};

const reportInvalidSharedSpaceUrlPrefix = (
  configKey: SharedSpaceUrlConfigKey,
  result: SharedSpaceUrlPrefixNormalization,
): void => {
  if (!result.reason) return;

  const reportKey = `${configKey}:${result.reason}`;
  if (reportedInvalidPrefixes.has(reportKey)) return;
  reportedInvalidPrefixes.add(reportKey);

  withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("area", "shared-space-sharing");
    scope.setTag("config_key", configKey);
    scope.setTag("reason", result.reason!);
    scope.setTag("value_type", result.valueType);
    scope.setFingerprint([
      "invalid-shared-space-prefix",
      configKey,
      result.reason!,
    ]);
    captureMessage("Invalid Shared Space URL prefix configuration");
  });
};

export const getConfiguredSharedSpaceUrlPrefix = (
  config: unknown,
  configKey: SharedSpaceUrlConfigKey,
): string | null => {
  if (
    typeof config !== "object" ||
    config === null ||
    !Object.prototype.hasOwnProperty.call(config, configKey)
  ) {
    return null;
  }

  const result = normalizeSharedSpaceUrlPrefix(
    (config as Record<string, unknown>)[configKey],
  );
  if (!result.value) {
    reportInvalidSharedSpaceUrlPrefix(configKey, result);
  }
  return result.value;
};

export const resolveSharedSpaceUrlPrefixes = (
  config: unknown,
  fallbackPrefix: string,
): SharedSpaceUrlPrefixes => {
  const configuredPrimary = getConfiguredSharedSpaceUrlPrefix(
    config,
    "shared_space_url_prefix",
  );
  const sharedSpaceUrlPrefix = configuredPrimary ?? fallbackPrefix;
  const defaultSpacePrefix = getConfiguredSharedSpaceUrlPrefix(
    config,
    "default_space_prefix",
  );

  return {
    sharedSpaceUrlPrefix,
    defaultSpacePrefix,
    hasBusinessCardVariants:
      defaultSpacePrefix !== null &&
      defaultSpacePrefix !== sharedSpaceUrlPrefix,
  };
};

export const resolveSharedSpaceLinkPrefix = (
  urlPrefix: unknown,
  fallbackPrefix: string,
): string => normalizeSharedSpaceUrlPrefix(urlPrefix).value ?? fallbackPrefix;

export const resolveDirectPreviewUrlPrefix = (
  defaultSpacePrefix: string | null | undefined,
  sharedSpaceUrlPrefix: string,
): string => defaultSpacePrefix ?? sharedSpaceUrlPrefix;
